import React, { useState, useEffect } from 'react';
import { X, Save, FileText, CheckCircle } from 'lucide-react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAppStore } from '../../store';
import { useToast } from '../../hooks/useToast';

interface TemplateEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (template: any) => void;
}

const TemplateEditorModal: React.FC<TemplateEditorModalProps> = ({
  isOpen,
  onClose,
  onSave
}) => {
  const { getActiveServiceAgreementTemplate, updateServiceAgreementTemplate, addServiceAgreementTemplate, activateServiceAgreementTemplate } = useAppStore();
  const { toast, ToastComponent } = useToast();

  const [template, setTemplate] = useState({
    id: '',
    name: 'Acuerdo de Servicio Productex',
    content: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadActiveTemplate();
    }
  }, [isOpen]);

  const loadActiveTemplate = async () => {
    try {
      setLoading(true);
      const activeTemplate = await getActiveServiceAgreementTemplate();

      if (activeTemplate) {
        setTemplate({
          id: activeTemplate.id,
          name: activeTemplate.name,
          content: activeTemplate.content
        });
      } else {
        setTemplate({
          id: '',
          name: 'Acuerdo de Servicio Productex',
          content: `En la Ciudad Autónoma de Buenos Aires, Entre {{client_name}} (en adelante, el "Cliente"), Documento Nacional de Identidad / CUIT / CUIL {{client_id}}, email {{client_email}} y RGX GROUP SRL, CUIT 30-71892824-5 con su marca Productex (en adelante, el "Proveedor"), ambas partes con plena capacidad legal para contratar, deciden suscribir el presente contrato de Servicio conforme el Código Civil y Comercial de la Nación Argentina y las siguientes cláusulas y condiciones:

1. OBJETO DEL ACUERDO - PLAZO

El presente acuerdo tiene por objeto regular los términos y condiciones bajo los cuales el Proveedor realizará la producción de las prendas solicitadas por el Cliente. Los detalles específicos de los artículos, cantidades y montos corresponden a la ficha técnica aprobada por el cliente.

Antes del inicio de la producción, el Proveedor enviará al Cliente una ficha técnica con las especificaciones de cada prenda. El Cliente deberá aprobar dicha ficha antes de iniciar la fabricación. Una vez aprobada, no se permitirán cambios o modificaciones de ningún tipo en la producción.

El presente contrato no supone plazos futuros. Los mismos resultarán SOLO del tracto sucesivo que AMBAS PARTES impartan en la consecución de la relación comercial objeto del presente. Resultando que cada pedido – orden de servicio, resultará consecuente a si misma y se entenderá por finalizada individualmente. Sin que la existencia de una orden de servicio presuponga la concurrencia de otra.

2. PAGOS

El Cliente, previa factura de El Proveedor, se compromete a abonar los montos especificados, tanto el adelanto antes de comenzar la producción, como así también el saldo, antes de la entrega de la misma. Destacando que NO se iniciará la producción sin el pago del adelanto. El saldo deberá ser abonado antes de la entrega de los productos indefectiblemente. Del mismo modo NO se entregará el pedido a El Cliente hasta que éste último haya realizado pago y acreditación efectiva del mismo.

3. PLAZOS DE ENTREGA

Los tiempos de producción estimados serán de {{production_days}} días hábiles, contados desde la fecha de APROBACION DEL PEDIDO Y PAGO DEL ADELANTO, lo que ocurra posterior. No obstante ello, el Cliente entiende y acepta que pueden eventualmente existir demoras debido a factores operativos, disponibilidad de insumos y/o situaciones imprevistas, la cual no podrá ser mayor a 45 días hábiles. El Proveedor notificará al Cliente en caso de cualquier demora significativa dentro de los parámetros fijados. En ningún caso las demoras podrán ser consideradas incumplimiento contractual salvo que resulten responsabilidad directa de El Proveedor.

4. CONTROL DE CALIDAD Y ARTÍCULOS DE SEGUNDA

El Cliente acepta plenamente que hasta un 5% del total de la producción pueda contener artículos de segunda calidad (prendas con detalles que no afectan su utilización o comercialización con o sin descuento). Estos artículos serán facturados al Cliente con un 50% de descuento sobre el precio acordado. En tal sentido – de eventualmente ocurrir lo anteriormente expuesto - El Cliente se compromete a recibir los respectivos productos en plena conformidad. Los artículos con fallas que excedan este 5% no serán entregados ni cobrados al Cliente.

5. PROPIEDAD DE LOS MATERIALES

Para el caso que el Cliente suministrara materiales (telas, insumos, etc.), deberá garantizar ante El Proveedor que resulten suficientes y adecuados para la producción, asumiendo la absoluta responsabilidad ante éste último. Así Productex no será responsable por defectos en la producción derivados de materiales, telas o insumos defectuosos proporcionados por El Cliente, incluso habiéndolos destinado al proceso productivo.

El Proveedor se reserva el derecho de rechazar materiales, en cualquier momento del proceso, que no cumplan con los estándares mínimos de calidad requeridos para la fabricación.

6. MODIFICACIONES EN EL PEDIDO

Cualquier cambio en cantidades, modelos o especificaciones después del inicio de la producción deberá ser aprobado por escrito y podría implicar costos adicionales. Si dichos cambios afectan significativamente los tiempos de producción, el Proveedor notificará al Cliente sobre el nuevo plazo estimado de entrega.

7. CASO FORTUITO - FUERZA MAYOR

El Proveedor no será responsable por demoras o incumplimientos debidos a causas de fuerza mayor, tales como accidentes, huelgas, falta de insumos, problemas climáticos y/o cualquier otro evento fuera del control razonable y de buena fé del Proveedor. Es compromiso del Proveedor mantener informado al Cliente sobre estos eventos. Es responsabilidad del Proveedor resolver con la mayor eficiencia posible los obstáculos que puedan surgir, siempre en virtud del beneficio del Cliente.

8. CONFORMIDAD Y RECEPCIÓN

El Cliente deberá revisar la mercadería al momento de la entrega. Pasadas 48 horas hábiles de la recepción, no se aceptarán reclamos por faltantes y/o defectos en la producción. En caso de disconformidad dentro de este plazo, el Cliente deberá presentar su reclamo por escrito, acompañado de evidencia fotográfica si correspondiera, para su evaluación por parte del Proveedor. Sin que la recepción de material de análisis por parte de El Proveedor suponga aceptación alguna del reclamo en cuestión.

9. JURISDICCION:

Ambas partes, entienden y aceptan que ante cualquier tipo de controversia que pudiera derivarse del presente, se someten a los Tribunales Ordinarios de la Ciudad Autónoma de Buenos Aires, con expresa exclusión de cualquier otro fuero o jurisdicción, y previa lectura y ratificación suscriben 2 (dos) ejemplares de un mismo tenor y a un solo efecto en el lugar y fecha ab – initio indicado.`
        });
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Error al cargar el template actual');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (template.id) {
        await updateServiceAgreementTemplate(template.id, {
          name: template.name,
          content: template.content,
          updated_at: new Date().toISOString()
        });
      } else {
        const newTemplate = await addServiceAgreementTemplate({
          name: template.name,
          content: template.content,
          is_active: false
        });

        if (newTemplate) {
          await activateServiceAgreementTemplate(newTemplate.id);
        }
      }

      onSave(template);
      onClose();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error al guardar el template', 'Por favor intenta nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <FileText className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Editor de Template de Contrato
            </h2>
          </div>
          <Button
            variant="outline"
            size="sm"
            icon={<X className="h-4 w-4" />}
            onClick={onClose}
            disabled={saving}
          >
            Cerrar
          </Button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center p-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Cargando template...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nombre del Template
                    </label>
                    <Input
                      value={template.name}
                      onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                      placeholder="Nombre del template"
                      disabled={saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Variables Disponibles
                    </label>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                        {`{{client_name}}`}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                        {`{{client_id}}`}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                        {`{{client_email}}`}
                      </span>
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 text-xs rounded">
                        {`{{production_days}}`}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Usa estas variables en el texto del contrato. Se reemplazarán automáticamente con los datos del cliente.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex-1 p-6 overflow-hidden">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Contenido del Contrato
                </label>
                <textarea
                  value={template.content}
                  onChange={(e) => setTemplate({ ...template, content: e.target.value })}
                  className="w-full h-full min-h-[400px] p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Escribe el contenido del contrato aquí..."
                  disabled={saving}
                />
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="outline"
                onClick={onClose}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                icon={saving ? null : <Save className="h-4 w-4" />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Guardando...
                  </>
                ) : (
                  <>Guardar Template</>
                )}
              </Button>
            </div>
          </>
        )}
      </div>
      {/* Toast Notifications */}
      {ToastComponent}
    </div>
  );
};

export default TemplateEditorModal;
